import { ConvexReactClient } from "convex/react";
import { devtoolsEnabled, instrumentedWebSocket } from "@/lib/devtools/convex-socket";

// El cliente Convex es global: Vite, TanStack Router, hooks de @convex-dev/auth
// y nuestras screens lo importan desde aca. Antes era un singleton creado al
// cargar el modulo, lo que impedia el switch de deployment en runtime. Ahora es
// un getter: la primera lectura crea el cliente; setConvexUrl lo reemplaza y
// emite un CustomEvent("convex:url-changed") en window para que el provider
// re-monte. El plugin de Vite (devtools/vite-plugin.ts) puede gatillarlo via
// server.ws.send("heskala:convex-set-url", { url, siteUrl }).

export const CONVEX_URL_EVENT = "convex:url-changed";

export interface ConvexUrlPayload {
  url: string;
  siteUrl: string;
}

let _convex: ConvexReactClient | null = null;
let _currentUrl: string | null = null;

function resolveConvexUrl(): string {
  const fromEnv = import.meta.env.VITE_CONVEX_URL;
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  throw new Error(
    "VITE_CONVEX_URL is not set and window.location is not available. " +
      "En dev: Convex CLI lo actualiza en .env.local. " +
      "Si no existe, corre `npx convex dev` una vez para que el CLI lo escriba.",
  );
}

function buildClient(url: string): ConvexReactClient {
  return new ConvexReactClient(
    url,
    devtoolsEnabled() ? { webSocketConstructor: instrumentedWebSocket() } : undefined,
  );
}

/** Cliente actual. Crea uno en la primera lectura. */
export function getConvex(): ConvexReactClient {
  if (!_convex) {
    _currentUrl = resolveConvexUrl();
    _convex = buildClient(_currentUrl);
  }
  return _convex;
}

/** Reemplaza el cliente global por uno que apunta a la nueva URL.
 *  Cierra el socket del anterior, crea uno nuevo y emite el evento para que
 *  el provider re-monte. Idempotente: si la URL no cambio, no hace nada. */
export function setConvexUrl(payload: ConvexUrlPayload): ConvexReactClient {
  if (payload.url === _currentUrl) return _convex!;
  const next = buildClient(payload.url);
  // Cerrar el cliente viejo: cierra el WebSocket y libera los watchers. Si el
  // browser no implementa close() (algunas builds del SDK), lo dejamos caer y
  // el GC lo recoge al perder las referencias.
  try {
    _convex?.close?.();
  } catch {
    // ignore: en el peor caso quedan suscripciones colgadas hasta el unload
  }
  _convex = next;
  _currentUrl = payload.url;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONVEX_URL_EVENT, { detail: payload }));
  }
  return next;
}

/** Suscribe un handler al evento de cambio de URL. Devuelve la funcion de cleanup. */
export function onConvexUrlChange(handler: (p: ConvexUrlPayload) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const wrap = (e: Event) => handler((e as CustomEvent<ConvexUrlPayload>).detail);
  window.addEventListener(CONVEX_URL_EVENT, wrap);
  return () => window.removeEventListener(CONVEX_URL_EVENT, wrap);
}

// Compatibilidad con los imports existentes: `import { convex } from "@/lib/convex"`.
// Es un Proxy que delega a getConvex() en cada acceso, asi cuando alguien
// guarda `convex` en una variable al inicio del modulo y luego cambia el
// cliente, sigue viendo el nuevo (porque el Proxy siempre devuelve el actual).
// Esto evita tener que reescribir los 100+ call sites que hacen
// `useQuery(..., convex ?? someClient)` o `const c = convex; c.query(...)`.
export const convex: ConvexReactClient = new Proxy({} as ConvexReactClient, {
  get(_target, prop, receiver) {
    const client = getConvex();
    const value = Reflect.get(client, prop, receiver);
    return typeof value === "function" ? value.bind(client) : value;
  },
});